import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import {DialogProps} from '@mui/material/Dialog';
import parse from 'html-react-parser';
import {
  Box,
  Card,
  Checkbox,
  FormControl,
  FormControlLabel,
  formControlLabelClasses,
  FormGroup,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Rating,
  styled,
  TextField,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import * as Yup from 'yup';
import {useForm, Controller, FormProvider} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import {C, s} from '@fullcalendar/core/internal-common';
import {
  RHFEditor,
  RHFMultiCheckbox,
  RHFMultiSelect,
  RHFRadioGroup,
  RHFSelect,
  RHFTextField
} from 'src/components/hook-form';
import {spacing} from '@mui/system';
import {label} from 'yet-another-react-lightbox';
import {fi} from 'date-fns/locale';
import {objectOf} from 'prop-types';

interface Choice {
  id: number;
  value: string;
}

interface MatchChoice {
  id: number;
  Question: string;
  value: string;
}

interface CustomIcon {
  icon: React.ReactElement;
  label: string;
  id: number;
  key: string;
}

interface CustomIcons {
  [key: string]: CustomIcon;
}

interface RatingScaleConfig {
  ratingScale: number;
  ratingScaleType: string;
  customIcons: CustomIcons;
}

interface npsString {
  likely: string;
  notlikely: string;
}

interface BaseQuestion {
  id: string;
  question: string;
  mark?: number;
  questionType:
    | 'single'
    | 'multiple'
    | 'true_false'
    | 'match'
    | 'descriptive'
    | 'ratingscale'
    | 'star'
    | 'nps';
  category?: string;
  level?: string;
  tags?: string;
  addToBank?: boolean;
  disableComments?: boolean;
  makecommentmandatory?: boolean;
  markasMandatory?: boolean;
  averagecourseRating?: boolean;
  comment?: string;
}

interface SingleQuestion extends BaseQuestion {
  questionType: 'single';
  correctAnswer?: number;
  choices: Choice[];
}

interface MultipleQuestion extends BaseQuestion {
  questionType: 'multiple';
  correctAnswer?: number[];
  choices: Choice[];
}

interface TrueFalseQuestion extends BaseQuestion {
  questionType: 'true_false';
  correctAnswer?: number;
  answer: string;
}

interface MatchingQuestion extends BaseQuestion {
  questionType: 'match';
  correctMatches: MatchChoice[];
  additionalChoices: MatchChoice[];
}

interface DescriptiveQuestion extends BaseQuestion {
  questionType: 'descriptive';
}

interface RatingScaleQuestion extends BaseQuestion {
  questionType: 'ratingscale';
  ratingScale: RatingScaleConfig;
  selectedRating?: number;
}

interface StarQuestion extends BaseQuestion {
  questionType: 'star';
  starRating?: number;
  selectedStarRating?: number;
}

interface NPSQuestion extends BaseQuestion {
  questionType: 'nps';
  npsstring: npsString;
  selectednpsRating?: number;
}

type Question =
  | SingleQuestion
  | MultipleQuestion
  | TrueFalseQuestion
  | MatchingQuestion
  | DescriptiveQuestion
  | RatingScaleQuestion
  | NPSQuestion
  | StarQuestion;

interface FeedbackFormCreationProps {
  formquestionsData: Question[];
  feedbackname: string;
  introductiondata: string;
  thanksdata: string;
  id: number;
}

interface MyDialogProps extends DialogProps {
  // eslint-disable-next-line react/boolean-prop-naming
  open: boolean;
  PreviewQuestionData: FeedbackFormCreationProps | undefined;
  onClose: () => void;
}
const getColor = (index: number, hoverIndex: number | null) => {
  if (hoverIndex !== null) {
    // If the current index is above hoverIndex, set it to light grey
    if (index > hoverIndex) return 'lightgrey';

    // Set the color according to the structure
    if (index < 3) return '#f44336'; // Red
    if (index === 3 || index === 4 || index === 5) return '#ff8c00'; // Orange
    if (index === 6 || index === 7) return '#ffeb3b'; // Yellow
    if (index === 8 || index === 9 || index === 10) return '#4caf50'; // Green
  }

  return 'lightgrey'; // Default color if not hovered
};

const IconContainer = styled(Box, {
  shouldForwardProp: prop => prop !== 'selected'
})<{selected?: boolean}>(({theme, selected}) => ({
  textAlign: 'center',
  cursor: 'pointer',
  backgroundColor: selected ? theme.palette.primary.main : 'transparent',
  borderRadius: '8px',
  padding: '8px',
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  }
}));
const IconContainers = styled(Box, {
  shouldForwardProp: prop => prop !== 'selected'
})<{selected?: any}>(({theme, selected}) => ({
  textAlign: 'center',
  cursor: 'pointer',
  borderRadius: '8px',
  padding: '8px'
}));
const TrueFalse = [
  {id: 1, value: 'True'},
  {id: 2, value: 'False'}
];

const PreviewComponentFeedback: React.FC<MyDialogProps> = ({
  open,
  onClose,
  PreviewQuestionData,
  ...props
}) => {
  const [questions, setQuestions] = useState<Question[]>(
    PreviewQuestionData?.formquestionsData || []
  );
  const [hoverIndex, setHoverIndex] = React.useState<number | null>(null);
  const createValidationSchema = (questions: Question[]) => {
    const schemaShape: any = {};

    questions.forEach((question, index) => {
      const {
        questionType,
        makecommentmandatory,
        markasMandatory,
        disableComments,
        averagecourseRating
      } = question;
      console.log(
        'questionType',
        questionType,
        makecommentmandatory,
        markasMandatory
      );
      let baseSchema;
      switch (questionType) {
        case 'single':
          if (makecommentmandatory) {
            baseSchema = Yup.object().shape({
              comment: Yup.string().required('Comment is required'),
              choiceanswer: Yup.string().required('Select a choice')
            });
          } else if (markasMandatory) {
            baseSchema = Yup.object().shape({
              comment: Yup.string().required('Comment is required'),
              choiceanswer: Yup.string().required('Select a choice')
            });
          } else {
            baseSchema = Yup.object().shape({
              comment: Yup.string(),
              choiceanswer: Yup.string()
            });
          }
          break;
        case 'multiple':
          if (makecommentmandatory) {
            baseSchema = Yup.object().shape({
              comment: Yup.string().required('Comment is required'),
              multipleanswer: Yup.array().of(Yup.string())
            });
          } else if (markasMandatory) {
            baseSchema = Yup.object().shape({
              comment: Yup.string().required('Comment is required'),
              multipleanswer: Yup.array()
                .of(Yup.string().required('Select a choice')) // Ensure each string in the array is valid
                .min(1, 'Select at least one choice') // Ensure at least one choice is selected
                .required('Select a choice') // Make the entire array required
            });
          } else {
            baseSchema = Yup.object().shape({
              comment: Yup.string(),
              multipleanswer: Yup.array().of(Yup.string())
            });
          }
          break;
        case 'true_false':
          if (makecommentmandatory) {
            baseSchema = Yup.object().shape({
              comment: Yup.string().required('Comment is required'),
              truefalseanswer: Yup.string().required('Select a choice')
            });
          } else if (markasMandatory) {
            baseSchema = Yup.object().shape({
              comment: Yup.string().required('Comment is required'),
              truefalseanswer: Yup.string().required('Select a choice')
            });
          } else {
            baseSchema = Yup.object().shape({
              comment: Yup.string(),
              truefalseanswer: Yup.string()
            });
          }
          break;
        case 'descriptive':
          if (markasMandatory) {
            baseSchema = Yup.object().shape({
              comment: Yup.string().required('Comment is required')
            });
          } else {
            baseSchema = Yup.object().shape({
              comment: Yup.string()
            });
          }
          break;
        case 'ratingscale':
          if (makecommentmandatory) {
            baseSchema = Yup.object().shape({
              comment: Yup.string().required('Comment is required'),
              ratingscaleanswer: Yup.string().required('Give Rating')
            });
          } else if (markasMandatory) {
            baseSchema = Yup.object().shape({
              comment: Yup.string().required('Comment is required'),
              ratingscaleanswer: Yup.string().required('Give Rating')
            });
          } else {
            baseSchema = Yup.object().shape({
              comment: Yup.string(),
              ratingscaleanswer: Yup.string()
            });
          }
          break;
        case 'star':
          if (makecommentmandatory) {
            baseSchema = Yup.object().shape({
              comment: Yup.string().required('Comment is required'),
              starRating: Yup.number().required('Give Star Rating')
            });
          } else if (markasMandatory) {
            baseSchema = Yup.object().shape({
              comment: Yup.string().required('Comment is required'),
              starRating: Yup.number().required('Give Star Rating')
            });
          } else {
            baseSchema = Yup.object().shape({
              comment: Yup.string(),
              starRating: Yup.number()
            });
          }
          break;
        case 'nps':
          if (makecommentmandatory) {
            baseSchema = Yup.object().shape({
              comment: Yup.string().required('Comment is required'),
              npsRating: Yup.number().required('Select a choice')
            });
          } else if (markasMandatory) {
            baseSchema = Yup.object().shape({
              comment: Yup.string().required('Comment is required'),
              npsRating: Yup.number().required('Select a choice')
            });
          } else {
            baseSchema = Yup.object().shape({
              comment: Yup.string(),
              npsRating: Yup.number()
            });
          }
          break;
        default:
          baseSchema = Yup.object().shape({
            comment: Yup.string().required('Comment is required')
          });
          break;
      }
      schemaShape[`question${question.id}`] = baseSchema;
    });

    return Yup.object().shape(schemaShape);
  };
  const validationSchema = useMemo(
    () => createValidationSchema(questions),
    [questions]
  );
  console.log('questionsvalidations', validationSchema, questions);

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {questions}
  });

  const {
    control,
    reset,
    watch,
    setValue,
    handleSubmit,
    formState: {errors}
  } = methods;
  console.log('questionserrors', errors, methods);
  console.log('questionscontrol', control);
  const [enters, setEnters] = useState(false);

  useEffect(() => {
    setQuestions(PreviewQuestionData?.formquestionsData || []);
  }, [PreviewQuestionData]);
  const [errorid, setErrorIds] = useState<string[]>([]);
  console.log('errorids', errorid);
  const commentRefs = useRef<{[key: string]: HTMLInputElement | null}>({});
  useEffect(() => {
    const errorKeys = Object.keys(errors)
      .map(key => key.match(/\d+/)) // Extract the number from each key using a regex
      .filter(number => number !== null) // Ensure there's a valid number
      .map(match => match[0]); // Extract the actual number as a string

    // Update the state with the collected numbers
    setErrorIds(errorKeys);
  }, [errors]);
  const LineTextField = styled(TextField)(({theme}) => ({
    '& .MuiInputBase-root': {
      borderBottom: '1px solid',
      borderRadius: 0
    },
    '& .MuiInputBase-input': {
      padding: '10px 0'
    },
    '& .MuiInputLabel-root': {
      color: '#06D6A0'
    },
    '& .MuiInputLabel-shrink': {
      display: 'none'
    },
    '& .MuiFormLabel-root': {
      color: '#919EAB'
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: '#06D6A0'
    }
  }));

  const handleAnswerChange = useCallback(
    (questionIndex: number, value: any) => {
      const updatedQuestions = [...questions];
      const question = updatedQuestions[questionIndex];
      if (question.questionType === 'multiple') {
        const correctAnswers = question.correctAnswer || [];
        if (correctAnswers.includes(value)) {
          question.correctAnswer = correctAnswers.filter(
            answer => answer !== value
          );
        } else {
          question.correctAnswer = [...correctAnswers, value];
        }
      } else if (question.questionType === 'single') {
        question.correctAnswer = value;
      } else if (question.questionType === 'true_false') {
        question.correctAnswer = value;
      }
      setQuestions(updatedQuestions);
    },
    [questions]
  );

  const handleCommentChange = useCallback(
    (questionIndex: number, commenting: string) => {
      const updatedQuestions = [...questions];
      updatedQuestions[questionIndex].comment = commenting;
      setQuestions(updatedQuestions);
    },
    [questions]
  );

  useEffect(() => {
    const currentRef = commentRefs.current;
    Object.keys(currentRef).forEach(key => {
      if (currentRef[key]) {
        currentRef[key]?.focus();
      }
    });
  }, [questions]);
  const onSubmit = handleSubmit(async data => {
    console.log('previewdata', data);
  });
  const getIconsForScale = (scale: number, type: any) => {
    const icons = type;
    switch (scale) {
      case 3:
        return [icons[1], icons[4], icons[7]];
      case 5:
        return [icons[1], icons[2], icons[4], icons[5], icons[7]];
      case 7:
        return [
          icons[1],
          icons[2],
          icons[3],
          icons[4],
          icons[5],
          icons[6],
          icons[7]
        ];
      default:
        return [];
    }
  };

  // const handleMouseEnter = (index: React.SetStateAction<number | null>) => {
  //   setHoveredIndex(index);
  // };

  // const handleMouseLeave = () => {
  //   setHoveredIndex(null);
  // };
  const renderQuestionOptions = (question: Question, questionIndex: number) => {
    switch (question.questionType) {
      case 'single':
        return (
          <div>
            <Box>
              <RHFRadioGroup
                name={`question${question.id}.choiceanswer`}
                key={question.id}
                label="Single Select"
                options={question.choices.map((option, index) => ({
                  value: option.value,
                  label: option.value
                }))}
              />
              <Controller
                name={`question${question.id}.choiceanswer`}
                control={control}
                render={({field, fieldState: {error}}) => (
                  <FormControl component="fieldset">
                    <FormLabel
                      component="legend"
                      id={`question${question.id}-Single Select`}
                      sx={{typography: 'body2'}}>
                      {'Single Select'}
                    </FormLabel>

                    <RadioGroup
                      {...field}
                      aria-labelledby={`question${question.id}-Single Select`}>
                      {question.choices.map(option => (
                        <FormControlLabel
                          key={option.value}
                          value={option.value}
                          control={<Radio />}
                          label={option.value}
                        />
                      ))}
                    </RadioGroup>

                    {/* {!!error && setErrorids(question.id)} */}
                  </FormControl>
                )}
              />
              <Controller
                name={`question${question.id}.comment`}
                control={control}
                render={({field, fieldState: {error}}) => (
                  <TextField
                    {...field}
                    label="Comment"
                    fullWidth
                    value={field.value}
                    onChange={event => {
                      field.onChange(event.target.value);
                    }}
                    error={!!error}
                    sx={{mt: 1}}
                  />
                )}
              />
              {/* <RHFEditor simple name={`question${question.id}.comment`} /> */}

              {/* {!question.disableComments && (
                <div>
                  <Controller
                    name={`question${questionIndex}.comment`}
                    control={control}
                    render={({field, fieldState: {error}}) => (
                      <LineTextField
                        key={question.id}
                        label="Comment"
                        variant="standard"
                        name={`question${questionIndex}.comment`}
                        fullWidth
                        inputRef={ref =>
                          (commentRefs.current[question.id] = ref)
                        }
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </div>
              )} */}
            </Box>
          </div>
        );
      case 'multiple':
        return (
          <Box sx={{display: 'grid'}}>
            <RHFMultiCheckbox
              name={`question${question.id}.multipleanswer`}
              key={question.id}
              label="Multiple Select"
              options={question.choices.map((option, index) => ({
                value: option.value,
                label: option.value
              }))}
            />

            <Controller
              name={`question${question.id}.comment`}
              control={control}
              render={({field, fieldState: {error}}) => (
                <TextField
                  {...field}
                  label="Comment"
                  fullWidth
                  value={field.value}
                  onChange={event => {
                    field.onChange(event.target.value);
                  }}
                  error={!!error}
                  sx={{mt: 1}}
                />
              )}
            />
            {/* <RHFEditor simple name={`question${question.id}.comment`} /> */}
          </Box>
        );
      case 'true_false':
        return (
          // <RadioGroup
          //   value={question.answer}
          //   onChange={e =>
          //     handleAnswerChange(questionIndex, e.target.value.toString())
          //   }>
          //   {['True', 'False'].map((option, index) => (
          //     <FormControlLabel
          //       key={index}
          //       value={option}
          //       control={<Radio checked={question.answer === option} />}
          //       label={option}
          //     />
          //   ))}
          // </RadioGroup>
          <Box>
            <RHFRadioGroup
              name={`question${question.id}.truefalseanswer`}
              key={question.id}
              label="True False"
              options={TrueFalse.map((option, index) => ({
                value: option.value,
                label: option.value
              }))}
            />
            <Controller
              name={`question${question.id}.comment`}
              control={control}
              render={({field, fieldState: {error}}) => (
                <TextField
                  {...field}
                  label="Comment"
                  fullWidth
                  value={field.value}
                  onChange={event => {
                    field.onChange(event.target.value);
                  }}
                  error={!!error}
                  sx={{mt: 1}}
                />
              )}
            />
          </Box>
        );
      case 'descriptive':
        return (
          <Box>
            <Controller
              name={`question${question.id}.comment`}
              control={control}
              render={({field, fieldState: {error}}) => (
                <TextField
                  {...field}
                  label="Comment"
                  fullWidth
                  value={field.value}
                  onChange={event => {
                    field.onChange(event.target.value);
                  }}
                  error={!!error}
                  sx={{mt: 1}}
                />
              )}
            />
          </Box>
        );
      case 'ratingscale':
        return (
          <Box>
            <Controller
              name={`question${question.id}.ratingscaleanswer`}
              control={control}
              render={({field, fieldState: {error}}) => (
                <FormControl component="fieldset">
                  {/* {label && (
                    <FormLabel component="legend" sx={{typography: 'body2'}}>
                      {label}
                    </FormLabel>
                  )} */}

                  <FormGroup sx={{display: 'flex', flexDirection: 'row'}}>
                    {getIconsForScale(
                      question.ratingScale?.ratingScale,
                      question.ratingScale?.customIcons
                    ).map(icon => (
                      <FormControlLabel
                        key={icon.id}
                        label=""
                        control={
                          <IconContainer
                            key={icon.id}
                            selected={icon.label === field.value}
                            onClick={() => {
                              console.log('icon', icon);
                              field.onChange(icon.label);
                            }}>
                            {icon.icon}
                            <Typography
                              variant="body2"
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}>
                              {icon.label}
                            </Typography>
                          </IconContainer>
                        }
                      />
                    ))}
                  </FormGroup>
                  {!!error && (
                    <FormHelperText error={!!error} sx={{mx: 0}}>
                      {error && error?.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
            <Controller
              name={`question${question.id}.comment`}
              control={control}
              render={({field, fieldState: {error}}) => (
                <TextField
                  {...field}
                  label="Comment"
                  fullWidth
                  value={field.value}
                  onChange={event => {
                    field.onChange(event.target.value);
                  }}
                  error={!!error}
                  sx={{mt: 1}}
                />
              )}
            />
          </Box>
        );
      case 'star':
        return (
          <Box>
            <Controller
              name={`question${question.id}.starRating`}
              control={control}
              render={({field, fieldState: {error}}) => (
                <FormControl component="fieldset">
                  {/* {label && (
                    <FormLabel component="legend" sx={{typography: 'body2'}}>
                      {label}
                    </FormLabel>
                  )} */}

                  <FormGroup sx={{display: 'flex', flexDirection: 'row'}}>
                    <Rating
                      name="star-rating"
                      value={field.value || 0}
                      onChange={(event, newValue) => {
                        console.log('fiels', newValue);
                        field.onChange(newValue);
                      }}
                      max={question.starRating}
                      size="medium"
                    />
                  </FormGroup>
                  {!!error && (
                    <FormHelperText error={!!error} sx={{mx: 0}}>
                      {error && error?.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
            <Controller
              name={`question${question.id}.comment`}
              control={control}
              render={({field, fieldState: {error}}) => (
                <TextField
                  {...field}
                  label="Comment"
                  fullWidth
                  value={field.value}
                  onChange={event => {
                    field.onChange(event.target.value);
                  }}
                  error={!!error}
                  sx={{mt: 1}}
                />
              )}
            />
          </Box>
          // <Rating
          //   name="star-rating"
          //   value={question.selectedStarRating || 0}
          //   onChange={(event, newValue) =>
          //     handleAnswerChange(questionIndex, newValue)
          //   }
          //   max={question.starRating}
          //   size="medium"
          // />
        );
      case 'nps':
        return (
          <Box>
            <Controller
              name={`question${question.id}.npsRating`}
              control={control}
              render={({field, fieldState: {error}}) => (
                <FormControl component="fieldset">
                  {/* {label && (
                    <FormLabel component="legend" sx={{typography: 'body2'}}>
                      {label}
                    </FormLabel>
                  )} */}

                  <FormGroup
                    sx={{display: 'flex', flexDirection: 'row', ml: '5px'}}>
                    <ToggleButtonGroup
                      exclusive
                      aria-label="answer score"
                      fullWidth
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-around'
                      }}>
                      {[...Array(11)].map((_, index) => (
                        <FormControlLabel
                          key={index}
                          label=""
                          control={
                            <IconContainers
                              key={index}
                              selected={index === field.value}
                              onMouseEnter={() => setHoverIndex(index)}
                              onMouseLeave={() => setHoverIndex(null)}
                              onClick={() => {
                                console.log('index', index);
                                field.onChange(index);
                              }}
                              sx={{
                                backgroundColor: hoverIndex
                                  ? getColor(index, hoverIndex)
                                  : getColor(index, field.value),
                                '&:hover': {
                                  backgroundColor: getColor(index, hoverIndex)
                                },
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '40px', // Adjust the size to your preference
                                height: '40px'
                              }}>
                              {index}
                            </IconContainers>
                          }
                        />
                      ))}
                    </ToggleButtonGroup>
                  </FormGroup>
                  {/* {!!error && (
                    <FormHelperText error={!!error} sx={{mx: 0}}>
                      {error && error?.message}
                    </FormHelperText>
                  )} */}
                </FormControl>
              )}
            />
            {/* <RHFTextField
              name={`question${question.id}.comment`}
              label="Comment"
              sx={{mt: 1}}
            /> */}
            <Controller
              name={`question${question.id}.comment`}
              control={control}
              render={({field, fieldState: {error}}) => (
                <TextField
                  {...field}
                  label="Comment"
                  fullWidth
                  value={field.value}
                  onChange={event => {
                    field.onChange(event.target.value);
                  }}
                  error={!!error}
                  sx={{mt: 1}}
                />
              )}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      {...props}
      maxWidth="md"
      sx={{
        '& .MuiDialog-paper': {
          minHeight: '91vh',
          minWidth: '105vh',
          overflowY: 'hidden !important',
          maxWidth: '105vh',
          justifyContent: `${!enters ? 'center' : 'flex-start'}`,
          backgroundColor: '#c9fdfd'
        }
      }}>
      {!enters && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-evenly'
          }}>
          <Card>
            <div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '0.5vh'
                }}>
                <Typography>{PreviewQuestionData?.feedbackname}</Typography>
              </div>
              <DialogContent>
                <Typography variant="h3" sx={{padding: '0.5vh'}}>
                  {parse(PreviewQuestionData?.introductiondata || '')}
                </Typography>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '0.5vh'
                  }}>
                  <Button
                    variant="contained"
                    color="info"
                    onClick={() => setEnters(true)}>
                    Enter the Feedback
                  </Button>
                </div>
              </DialogContent>
            </div>
          </Card>
        </div>
      )}
      {enters && (
        <FormProvider {...methods}>
          <form onSubmit={onSubmit}>
            <div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '0.5vh',
                  fontSize: '16px !important',
                  fontWeight: 'bold'
                }}>
                <Typography>{PreviewQuestionData?.feedbackname}</Typography>
              </div>
            </div>
            <div
              style={{maxHeight: '80vh', minHeight: '79vh', overflow: 'auto'}}>
              {questions.map((question, index) => (
                <Card
                  key={question.id}
                  sx={{
                    padding: '1vh',
                    margin: '1vh',
                    backgroundColor: 'whitesmoke',
                    borderColor: 'red',
                    borderWidth: '2px',
                    borderStyle: `${index +1}inset`
                  }}>
                  <Typography variant="h6">{`Question ${index + 1}`}</Typography>
                  <Typography sx={{fontSize: '20px !important'}}>
                    {question.question}
                  </Typography>
                  <Box
                    sx={{
                      padding: '1vh',
                      borderRadius: '4px',
                      display: 'grid'
                    }}>
                    {renderQuestionOptions(question, index)}
                  </Box>
                </Card>
              ))}
            </div>
            <div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '0.5vh'
                }}>
                <Button variant="contained" color="info" onClick={onSubmit}>
                  Submit
                </Button>
              </div>
            </div>
          </form>
        </FormProvider>
      )}
    </Dialog>
  );
};

export default PreviewComponentFeedback;
